import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Geometry, XZ } from '../../geo/types';
import { geometryAnchor, geometryStats, rectCorners, chunkAt } from '../../geo/measure';
import { csharpVector3, num, pointsJson, unityInspectorVector3 } from '../../geo/format';
import type { FeatureRecord } from '../../lib/types';
import { isEditor, toast, useCurrentMap, useStore } from '../../store/store';
import { updateFeature } from '../../store/actions';
import { sampleHeight } from '../../store/data';

function copy(text: string, done: string) {
  void navigator.clipboard?.writeText(text).then(() => toast(done));
}

export function GeometryTab({ feature: f }: { feature: FeatureRecord }) {
  const { t } = useTranslation();
  const editor = isEditor(useStore((s) => s.user));
  const map = useCurrentMap();
  const g = f.geometry;
  const stats = geometryStats(g);
  const anchor = geometryAnchor(g);
  const y = anchor ? (g.type === 'point' && g.y != null ? g.y : sampleHeight(anchor.x, anchor.z)) : null;
  const origin = map?.origin ?? { x: map?.worldRect.minX ?? 0, z: map?.worldRect.minZ ?? 0 };
  const chunk = anchor && map ? chunkAt(anchor.x, anchor.z, origin, map.chunkSize, map.chunks ?? []) : null;
  const copied = t('common.copied');

  const points: XZ[] | null =
    g.type === 'polygon' || g.type === 'polyline' ? g.points : g.type === 'rect' ? rectCorners(g) : null;

  return (
    <div className="geom">
      <dl className="stats">
        <dt>{t('geometry.kind')}</dt>
        <dd>{t(`geometry.kinds.${g.type}`)}</dd>
        {anchor && (
          <>
            <dt>{g.type === 'point' ? t('geometry.position') : t('geometry.center')}</dt>
            <dd className="mono">
              X {num(anchor.x)} · Z {num(anchor.z)}
              {y !== null && ` · Y ${num(y)}`}
            </dd>
          </>
        )}
        {chunk && (
          <>
            <dt>{t('geometry.chunk')}</dt>
            <dd className="mono">{chunk.sceneName}</dd>
          </>
        )}
        {stats.area !== undefined && (
          <>
            <dt>{t('geometry.area')}</dt>
            <dd>
              {num(stats.area, 0)} m² ({num(stats.area / 10000, 2)} ha)
            </dd>
          </>
        )}
        {stats.perimeter !== undefined && (
          <>
            <dt>{t('geometry.perimeter')}</dt>
            <dd>{num(stats.perimeter, 1)} m</dd>
          </>
        )}
        {stats.length !== undefined && (
          <>
            <dt>{t('geometry.length')}</dt>
            <dd>{num(stats.length, 1)} m</dd>
          </>
        )}
        {stats.vertices !== undefined && (
          <>
            <dt>{t('geometry.vertices')}</dt>
            <dd>{stats.vertices}</dd>
          </>
        )}
      </dl>
      {y === null && anchor && <p className="muted small">{t('geometry.yUnknown')}</p>}

      {anchor && (
        <div className="row wrap">
          <button onClick={() => copy(unityInspectorVector3(anchor.x, y ?? 0, anchor.z), copied)}>
            {t('geometry.copyVector3')}
          </button>
          <button onClick={() => copy(csharpVector3(anchor.x, y ?? 0, anchor.z), copied)}>
            {t('geometry.copyCSharp')}
          </button>
          {points && (
            <button onClick={() => copy(pointsJson(points), copied)}>{t('geometry.copyPoints')}</button>
          )}
        </div>
      )}

      {editor && <NumericEditor key={`${f.id}-${f.rev}`} feature={f} />}

      {points && (
        <table className="vertices mono small">
          <thead>
            <tr>
              <th>#</th>
              <th>X</th>
              <th>Z</th>
            </tr>
          </thead>
          <tbody>
            {points.map(([x, z], i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{num(x)}</td>
                <td>{num(z)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Type exact numbers (e.g. from Unity) for points, circles and rects. */
function NumericEditor({ feature: f }: { feature: FeatureRecord }) {
  const { t } = useTranslation();
  const g = f.geometry;
  const fields: Record<string, string[]> = {
    point: ['x', 'z', 'rotY'],
    circle: ['x', 'z', 'r'],
    rect: ['x', 'z', 'w', 'd'],
  };
  const names = fields[g.type];
  const [vals, setVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (names ?? []).map((n) => [
        n,
        String((g as unknown as Record<string, number | null | undefined>)[n] ?? ''),
      ]),
    ),
  );
  if (!names) return null;
  const valid = names.every((n) =>
    n === 'rotY' ? vals[n] === '' || isFinite(Number(vals[n])) : vals[n] !== '' && isFinite(Number(vals[n])),
  );
  const apply = () => {
    const next = { ...g } as unknown as Record<string, unknown>;
    for (const n of names) next[n] = vals[n] === '' ? undefined : Number(vals[n]);
    void updateFeature(f.id, { geometry: next as unknown as Geometry });
  };
  return (
    <div className="numeric">
      {names.map((n) => (
        <label key={n}>
          {t(`geometry.fields.${n}`)}
          <input
            type="number"
            step="any"
            value={vals[n]}
            onChange={(e) => setVals((v) => ({ ...v, [n]: e.target.value }))}
          />
        </label>
      ))}
      <button onClick={apply} disabled={!valid}>
        {t('common.apply')}
      </button>
    </div>
  );
}
